import { FC, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import { FieldArray, FormikProvider, useFormik } from "formik";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { z } from "zod";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { makeSxStyles } from "ui";
import {
  useAdminElevationLevelControllerCreate,
  useAdminElevationLevelControllerDelete,
  useAdminElevationLevelControllerUpdate,
} from "service";
import { AdminElevationLevelResDto } from "service/api/estabrookSchemas";
import { queryKeyFn } from "service/api/estabrookContext";
import { ErrorWrapper } from "service/api/estabrookFetcher";
import { skipGlobalErrorToast } from "@/query-client";

interface EditElevationLevelsModalProps {
  open: boolean;
  onClose: () => void;
  levels: AdminElevationLevelResDto[];
  onSaved?: () => void;
}

interface LevelRow {
  id?: string;
  min: number | "";
  max: number | "";
}

const levelRowSchema = z
  .object({
    id: z.string().optional(),
    min: z.number({ invalid_type_error: "Required" }).min(0, "Must be ≥ 0"),
    max: z.number({ invalid_type_error: "Required" }),
  })
  // Messages stay short so they fit the dialog's 126px field column.
  .refine((row) => row.max > row.min, {
    message: "Must exceed min",
    path: ["max"],
  });

const formSchema = z.object({
  levels: z
    .array(levelRowSchema)
    .min(1, "At least one elevation level is required"),
});

const DUPLICATE_MESSAGE = "Duplicate range";
const OVERLAP_MESSAGE = "Overlapping range";

/**
 * Mirrors the backend's `assertNoOverlap`: flags every row involved in a
 * duplicate or overlapping range (pairwise), keyed by row index.
 *
 * Deliberately outside the zod schema — a row that fails its own validation
 * (an empty min/max) aborts the array parse, which would skip an array-level
 * refinement and hide this error behind unrelated incomplete rows.
 */
const findRangeErrors = (rows: LevelRow[]): Record<number, string> => {
  const ranges = rows
    .map((row, index) => ({ min: row.min, max: row.max, index }))
    .filter(
      (row) => typeof row.min === "number" && typeof row.max === "number",
    ) as { min: number; max: number; index: number }[];

  const errors: Record<number, string> = {};
  // An exact duplicate is the more specific diagnosis, so it wins over a
  // plain overlap when a row collides with several others.
  const flag = (index: number, message: string) => {
    if (errors[index] === DUPLICATE_MESSAGE) return;
    errors[index] = message;
  };

  for (let a = 0; a < ranges.length; a++) {
    for (let b = a + 1; b < ranges.length; b++) {
      const first = ranges[a];
      const second = ranges[b];
      if (first.min >= second.max || second.min >= first.max) continue;
      const message =
        first.min === second.min && first.max === second.max
          ? DUPLICATE_MESSAGE
          : OVERLAP_MESSAGE;
      flag(first.index, message);
      flag(second.index, message);
    }
  }

  return errors;
};

/**
 * A delete that 404s means the level is already gone — the end state this save
 * was asking for, so it isn't worth failing (or reporting) over. Anything else
 * is a real error.
 *
 * The API's error body is `{ code, title, httpStatus, traceId, errors }` — no
 * numeric status, so match the machine-readable code the backend sends with a
 * NotFoundException.
 */
const isAlreadyGone = (error: unknown): boolean =>
  (error as ErrorWrapper<undefined>)?.errors?.[0]?.code === "NOT_FOUND";

const toRows = (levels: AdminElevationLevelResDto[]): LevelRow[] =>
  levels.map((level) => ({ id: level.id, min: level.min, max: level.max }));

const EditElevationLevelsModal: FC<EditElevationLevelsModalProps> = ({
  open,
  onClose,
  levels,
  onSaved,
}) => {
  const styles = useStyles();
  const queryClient = useQueryClient();
  // Formik has already flipped `isSubmitting` by the time onSubmit runs, so the
  // in-flight guard needs its own ref.
  const isSavingRef = useRef(false);

  // These three report their own failures below, so they opt out of the global
  // mutation toast rather than doubling up on it.
  const { mutateAsync: createLevel } = useAdminElevationLevelControllerCreate({
    meta: skipGlobalErrorToast,
  });
  const { mutateAsync: updateLevel } = useAdminElevationLevelControllerUpdate({
    meta: skipGlobalErrorToast,
  });
  const { mutateAsync: deleteLevel } = useAdminElevationLevelControllerDelete({
    meta: skipGlobalErrorToast,
  });

  const refetchLevels = () =>
    queryClient.refetchQueries(
      queryKeyFn({
        path: "/api/v1/admin/elevation-levels",
        operationId: "adminElevationLevelControllerFindMany",
        variables: {},
      }),
    );

  const form = useFormik<{ levels: LevelRow[] }>({
    initialValues: { levels: toRows(levels) },
    validationSchema: toFormikValidationSchema(formSchema),
    onSubmit: async ({ levels: rows }) => {
      // Save is disabled in this state, but Enter-to-submit can still reach
      // here — don't fire requests the backend will reject.
      if (Object.keys(findRangeErrors(rows)).length > 0) return;
      // A second Enter can submit again before the disabled Save button
      // re-renders, which replays the same deletes against ids that the first
      // save already removed.
      if (isSavingRef.current) return;
      isSavingRef.current = true;

      const normalized = rows.map((row) => ({
        id: row.id,
        min: Number(row.min),
        max: Number(row.max),
      }));

      const original = new Map(levels.map((level) => [level.id, level]));
      const keptIds = new Set(
        normalized.filter((row) => row.id).map((row) => row.id as string),
      );

      const created = normalized.filter((row) => !row.id);
      const updated = normalized.filter((row) => {
        if (!row.id) return false;
        const prev = original.get(row.id);
        return prev ? prev.min !== row.min || prev.max !== row.max : false;
      });
      const deletedIds = levels
        .map((level) => level.id)
        .filter((id) => !keptIds.has(id));

      try {
        // Sequential, deletes first: the backend rejects overlapping ranges, so
        // firing these together lets a create land while the range it reuses is
        // still held by a level this same save is removing.
        for (const id of deletedIds) {
          try {
            await deleteLevel({ pathParams: { id } });
          } catch (e) {
            if (!isAlreadyGone(e)) throw e;
          }
        }
        for (const row of updated) {
          await updateLevel({
            pathParams: { id: row.id as string },
            body: { min: row.min, max: row.max },
          });
        }
        for (const row of created) {
          await createLevel({ body: { min: row.min, max: row.max } });
        }
        toast.success("Elevation Levels Updated");
        onSaved?.();
      } catch (e) {
        // Backend stays authoritative (e.g. a range swap it can't accept
        // step by step) — surface its specific message when present.
        const detail = (e as ErrorWrapper<undefined>)?.errors?.[0]?.detail;
        toast.error(detail || "Failed to update elevation levels");
      } finally {
        // There is no bulk endpoint, so a failure part way through leaves the
        // earlier writes applied. Rather than reconcile a half-written form,
        // both outcomes end the same way: refetch and close, so the dialog
        // always opens on what the server actually holds.
        await refetchLevels();
        isSavingRef.current = false;
        onClose();
      }
    },
  });

  // Re-seed rows from the current org levels each time the dialog opens.
  useEffect(() => {
    if (open) {
      form.resetForm({ values: { levels: toRows(levels) } });
    }
  }, [open]);

  const handleClose = () => {
    form.resetForm({ values: { levels: toRows(levels) } });
    onClose();
  };

  const isEmpty = form.values.levels.length === 0;

  const rowErrors = useMemo(
    () =>
      (form.errors.levels as
        | Array<{ min?: string; max?: string } | undefined>
        | undefined) ?? [],
    [form.errors.levels],
  );

  const rangeErrors = useMemo(
    () => findRangeErrors(form.values.levels),
    [form.values.levels],
  );

  const disableSave =
    form.isSubmitting ||
    !form.isValid ||
    isEmpty ||
    Object.keys(rangeErrors).length > 0;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      // Don't return focus to the "Edit" trigger on close: after an Enter-key
      // save that leaves the trigger showing a focus-visible ring that reads
      // as a stuck hover state.
      disableRestoreFocus
      sx={styles.dialog}
    >
      <DialogTitle sx={styles.title}>
        <Typography sx={styles.headline}>Edit Elevation Levels</Typography>
        <Typography sx={styles.supportingText}>
          Define the elevation ranges for snow reporting.
        </Typography>
      </DialogTitle>
      <FormikProvider value={form}>
        <Box component="form" onSubmit={form.handleSubmit} sx={styles.form}>
          <DialogContent sx={styles.content}>
            <FieldArray name="levels">
              {({ push, remove }) => (
                <>
                  <Box sx={styles.rows}>
                    <Box sx={styles.rowHeader}>
                      <Typography sx={styles.columnLabel}>Min (ft)</Typography>
                      <Box />
                      <Typography sx={styles.columnLabel}>Max (ft)</Typography>
                    </Box>
                    {form.values.levels.map((row, index) => {
                      // Each field carries its own message so a row with both
                      // values missing shows one "Required" under each field
                      // rather than a single combined line. The range error is
                      // reported under min, behind that row's own field error.
                      const minError =
                        rowErrors[index]?.min ?? rangeErrors[index];
                      return (
                        <Box key={index} sx={styles.row}>
                          <TextField
                            type="number"
                            size="small"
                            value={row.min}
                            onChange={(e) =>
                              form.setFieldValue(
                                `levels.${index}.min`,
                                e.target.value === ""
                                  ? ""
                                  : Number(e.target.value),
                              )
                            }
                            error={Boolean(minError)}
                            helperText={minError}
                          />
                          <Box sx={styles.separatorCell}>
                            <Box sx={styles.separatorLine} />
                          </Box>
                          <TextField
                            type="number"
                            size="small"
                            value={row.max}
                            onChange={(e) =>
                              form.setFieldValue(
                                `levels.${index}.max`,
                                e.target.value === ""
                                  ? ""
                                  : Number(e.target.value),
                              )
                            }
                            error={Boolean(rowErrors[index]?.max)}
                            helperText={rowErrors[index]?.max}
                          />
                          <IconButton
                            aria-label="Remove level"
                            onClick={() => remove(index)}
                            data-testid="remove-elevation-level"
                          >
                            <Delete />
                          </IconButton>
                        </Box>
                      );
                    })}
                  </Box>
                  <Button
                    startIcon={<Add />}
                    onClick={() => push({ min: "", max: "" })}
                    data-testid="add-elevation-level"
                    sx={styles.addButton}
                  >
                    Add Level
                  </Button>
                  {isEmpty && (
                    <Box
                      sx={styles.emptyBanner}
                      data-testid="elevation-levels-error"
                    >
                      <Typography variant="body2">
                        Add at least one elevation level.
                      </Typography>
                    </Box>
                  )}
                </>
              )}
            </FieldArray>
          </DialogContent>
          <DialogActions sx={styles.actions}>
            <Button variant="text" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={disableSave}
              data-testid="save-elevation-levels"
              startIcon={
                form.isSubmitting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : undefined
              }
            >
              {form.isSubmitting ? "Saving..." : "Save Levels"}
            </Button>
          </DialogActions>
        </Box>
      </FormikProvider>
    </Dialog>
  );
};

// Row columns: min field · 24px range dash · max field · 40px remove button.
// At the dialog's 400px width (24px paper padding) this resolves to the
// design's 126px fields.
const ROW_COLUMNS = "1fr 24px 1fr 40px";

const useStyles = makeSxStyles(() => ({
  dialog: {
    // The theme paper already carries the design's 28px radius, #282A2D
    // surface and 24px padding — only the width is dialog-specific.
    "& .MuiDialog-paper": {
      width: "400px",
      maxWidth: "calc(100% - 64px)",
    },
  },
  title: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    mb: "24px",
  },
  headline: {
    fontSize: "22px",
    lineHeight: "28px",
    fontWeight: 400,
    color: "#C5C6C9",
  },
  supportingText: {
    fontSize: "11px",
    lineHeight: "14px",
    fontWeight: 500,
    color: "#C1C7CE",
  },
  // MUI hands the scroll to DialogContent, but only while it stays a flex child
  // of the paper. This form sits in between, so it has to pass the paper's
  // bounded height through instead of growing past it — otherwise the paper's
  // own `overflow-y: auto` (a legacy IE11 rule) takes over and scrolls the whole
  // dialog, drawing the scrollbar across its 28px rounded corners and carrying
  // the title and action buttons out of view.
  form: {
    display: "flex",
    flexDirection: "column",
    flex: "1 1 auto",
    minHeight: 0,
  },
  content: {
    display: "flex",
    flexDirection: "column",
    // Lets the rows area shrink below its content height so its own overflow
    // engages; without this a flex item refuses to go under min-content.
    minHeight: 0,
    // Park the scrollbar in the paper's 24px padding. Platforms with permanent
    // scrollbars (Windows, Linux) reserve a real gutter, which would otherwise
    // shave the design's 126px field columns.
    mr: "-12px",
    pr: "12px",
  },
  rows: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  rowHeader: {
    display: "grid",
    gridTemplateColumns: ROW_COLUMNS,
    gap: "12px",
    alignItems: "center",
  },
  columnLabel: {
    fontSize: "14px",
    lineHeight: "20px",
    fontWeight: 500,
    color: "#C1C7CE",
  },
  row: {
    display: "grid",
    gridTemplateColumns: ROW_COLUMNS,
    gap: "12px",
    // Top-aligned so the remove button keeps its place when a field grows a
    // helper message below it.
    alignItems: "start",
    // The theme positions helper text absolutely (bottom: -20px), which only
    // works for a single line — anything that wraps grows up over the input
    // and down into the next row. In flow it just makes the row taller.
    "& .MuiFormHelperText-root": {
      position: "static",
      margin: "4px 0 0",
    },
    // The design has plain fields — native number spinners would also eat
    // into the 126px column on hover.
    "& input[type=number]": { MozAppearance: "textfield" },
    "& input[type=number]::-webkit-outer-spin-button": {
      WebkitAppearance: "none",
      margin: 0,
    },
    "& input[type=number]::-webkit-inner-spin-button": {
      WebkitAppearance: "none",
      margin: 0,
    },
  },
  separatorCell: {
    height: "40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  separatorLine: {
    width: "10px",
    height: "2px",
    borderRadius: "1px",
    backgroundColor: "#C1C7CE",
  },
  addButton: {
    mt: "24px",
    width: "100%",
    height: "40px",
    padding: "10px 16px",
    borderRadius: "100px",
    backgroundColor: "#1A1C1E",
    color: "#8ACEFF",
    fontSize: "14px",
    fontWeight: 500,
    lineHeight: "20px",
    boxShadow:
      "0px 1px 3px 1px rgba(0, 0, 0, 0.15), 0px 1px 2px 0px rgba(0, 0, 0, 0.30)",
    "&:hover": {
      backgroundColor: "#232527",
    },
    "& .MuiButton-startIcon": {
      marginLeft: 0,
      marginRight: "8px",
      "& .MuiSvgIcon-root": {
        fontSize: "18px",
      },
    },
  },
  emptyBanner: {
    mt: "24px",
    backgroundColor: "#F2B8B5",
    color: "#601410",
    borderRadius: "12px",
    padding: "16px 20px",
  },
  actions: {
    // The design's 48px gap between "Add Level" and the buttons; the 24px
    // below them comes from the theme's paper padding.
    padding: "48px 0 0",
    gap: "8px",
  },
}));

export default EditElevationLevelsModal;
